import Reference from '@/fhirworks/types/Reference';
import merge from 'lodash/merge';
import DomainResource from './DomainResource';

/**
 * BN_MessageThread custom resource — a messaging conversation.
 *
 * `subject` is the client the thread is about; absent means a standalone thread with no
 * client context. `type` ('client' | 'internal') is deliberately NOT derived from `subject` —
 * an internal staff thread started from a client's chart has both a subject and staff-only
 * membership. `status` ('active' | 'closed') is the lifecycle; threads are closed, never
 * deleted. `lastMessageAt` is a denormalized sort key so the inbox can order threads without
 * loading their messages.
 *
 * `about` is the clients this conversation has been FILED against, and optionally a form belonging
 * to one of them (#5406). Filing, never membership: an entry here adds nobody to the conversation
 * and gives an associated client no way to read it — a client reads a conversation only by being a
 * participant in it. What it does do is let staff holding that client's chart read the conversation
 * whether or not they take part in it, which is what `/AccessPolicy/messaging-thread-read-associated`
 * and `/AccessPolicy/messaging-communication-read-associated` are for.
 *
 * `about` and `subject` are different questions and both are needed. `subject` is who a client
 * conversation is WITH — it is fixed at creation, it is at most one person, and it is what puts the
 * client in the roster. `about` is added after the fact, can name several clients, names none of them
 * as a participant, and never changes who a message was delivered to.
 *
 * `accessGroup` restricts who may see the conversation at all (#5405) — the same BN_AccessGroup
 * references BN_File and QuestionnaireResponse carry. Absent or empty means unrestricted; otherwise
 * a staff member must hold at least one of them, ON TOP OF participation and chart access. Fixed at
 * creation: no write path changes it, because editing it would retroactively change who can read
 * messages already sent.
 *
 * Membership lives on BN_MessageThreadParticipant; the messages are Communication resources
 * pointing back here via the `thread` first-class extension.
 */
export default class BN_MessageThread extends DomainResource {
    static __className = 'BN_MessageThread';
    __objectStructure = {
        subject: Reference,
        // The clients and the form this conversation is filed against (#5406). Beside `subject`
        // because they answer adjacent questions — see the class docblock for why both exist.
        about: [Reference],
        type: String,
        status: String,
        topic: String,
        created: String,
        author: Reference,
        accessGroup: [Reference], // BN_AccessGroup
        lastMessageAt: String,
    };

    constructor(constructJson, className = 'BN_MessageThread') {
        super(constructJson, className);
        this.createAndPopulateStructure(this.__objectStructure, constructJson, this.__objectDefaults);
        this.originalObjJson = this.toJSON();
    }

    toJSON() {
        return merge(super.toJSON(this), this.getJsonForStructure(this.__objectStructure));
    }
}
